import { useAppStore } from '@store'
import { saveProyect } from '@proxies/saveProyect'
import { useEffect, useRef, useState, useMemo, useCallback } from 'react'
import { Item, Zone } from '@types'

export function useAutoSave() {
  const [isSaving, setIsSaving] = useState(false)
  const [lastSaved, setLastSaved] = useState<Date | null>(null)
  const intervalRef = useRef<NodeJS.Timeout | null>(null)
  const isSavingRef = useRef(false)

  const lastSavedByProjectRef = useRef<Map<string, Date>>(new Map())

  const prefs = useAppStore((store) => store.prefs)
  const clientInfo = useAppStore((store) => store.clientInfo)

  const intervalMinutes = useMemo(() => {
    const rawValue = prefs?.['AUTOGUARDADO']

    const value =
      rawValue !== undefined && rawValue !== null && rawValue !== '' ? Number(rawValue) : 0

    return isNaN(value) ? 0 : value
  }, [prefs])

  const hasProjectName = useMemo(() => {
    const has = !!(clientInfo.nombreProyecto && clientInfo.nombreProyecto.trim() !== '')

    return has
  }, [clientInfo.nombreProyecto, clientInfo.idDisenos])

  const projectKey = useMemo(() => {
    return `${clientInfo.nombreProyecto}_${clientInfo.idDisenos}`
  }, [clientInfo.nombreProyecto, clientInfo.idDisenos])

  useEffect(() => {
    if (projectKey && lastSavedByProjectRef.current.has(projectKey)) {
      const projectLastSaved = lastSavedByProjectRef.current.get(projectKey)!
      setLastSaved(projectLastSaved)
    } else if (projectKey && clientInfo.idDisenos) {
      loadLastSavedFromBackend()
    } else {
      setLastSaved(null)
    }
  }, [projectKey, clientInfo.nombreProyecto, clientInfo.idDisenos])

  const loadLastSavedFromBackend = async () => {
    if (!clientInfo.idDisenos) return

    try {
      const store = useAppStore.getState()
      const projectInfo = store.clientInfo

      if (projectInfo.fechaUltimaModificacion) {
        const lastSavedDate = new Date(projectInfo.fechaUltimaModificacion)
        setLastSaved(lastSavedDate)
        lastSavedByProjectRef.current.set(projectKey, lastSavedDate)
      }
    } catch (error) {
      console.warn('⚠️ Error cargando último guardado desde backend:', error)
    }
  }
  const items = useAppStore((store) => store.items)
  const areas = useAppStore((store) => store.areas)
  const totalPrice = useAppStore((store) => store.price)
  const objectsScene = useAppStore((store) => store.layers[store.currentLayer].items)
  const sceneBoundingBoxes = useAppStore((store) => store.sceneBoundingBoxes)
  const savedViews = useAppStore((store) => store.savedViews)
  const currLayer = useAppStore((store) => store.currentLayer)
  const sceneGLB = useAppStore((store) => store.sceneGLB)
  const collisionableItems = useAppStore((store) => store.collisionableItems)

  const autoSave = useCallback(async () => {
    if (isSavingRef.current) {
      return
    }

    if (!clientInfo.nombreProyecto || clientInfo.nombreProyecto.trim() === '') {
      return
    }

    isSavingRef.current = true
    setIsSaving(true)

    try {
      const store = useAppStore.getState()

      if (store.restoreAllLights) {
        store.restoreAllLights()
        await new Promise((resolve) => setTimeout(resolve, 100))
      }

      // Guardar estado actual de las cotas y ocultarlas temporalmente
      const originalHideDimentions3D = store.hideDimentions3D ?? false
      store.setHideDimentions3D(true) // Ocultar cotas temporalmente

      // Esperar frames para que se renderice sin las cotas
      await new Promise((resolve) => requestAnimationFrame(resolve))
      await new Promise((resolve) => requestAnimationFrame(resolve))
      await new Promise((resolve) => setTimeout(resolve, 150))

      const canvas1 = document.querySelector('canvas') as HTMLCanvasElement
      let data1 = { nombre: '2D', imageUrl: '' }

      if (canvas1) {
        try {
          const imageUrl = canvas1.toDataURL('image/png', 1.0)
          data1 = { nombre: '2D', imageUrl }
        } catch (error) {
          data1 = { nombre: '2D', imageUrl: '' }
        }
      } else {
        console.warn('⚠️ No se encontró canvas para capturar')
      }

      // Restaurar estado original de las cotas
      store.setHideDimentions3D(originalHideDimentions3D)

      const visuals = [data1]

      const currentStore = useAppStore.getState()
      const currentObjectsScene = currentStore.layers[currentStore.currentLayer].items

      const objetos: { idUnidades: number; zonas: { nombre: string; idTexturas: number }[] }[] =
        Array.from(currentObjectsScene)
          .map((id) => {
            const currItem = currentStore.items.get(id) as Item
            let zones: { nombre: string; idTexturas: number }[]
            if (currItem?.textures?.length > 0) {
              zones = currItem?.textures.map((val: Zone) => ({
                nombre: val.name,
                idTexturas: val.id === '' ? 1 : (val.id ?? 1),
              }))
            } else {
              zones = [{ nombre: 'null', idTexturas: 1 }]
            }
            return { idUnidades: currItem?.idUnidades, zonas: zones }
          })
          .filter((currItem) => currItem.idUnidades !== undefined)

      const date = new Date().toISOString()

      const currentClientInfo = currentStore.clientInfo

      const result = await saveProyect(
        currentClientInfo,
        objetos,
        currentStore.price,
        date,
        visuals,
        currentStore.layers,
        currentStore.currentLayer,
        currentStore.items,
        currentStore.areas,
        currentStore.sceneBoundingBoxes,
        currentStore.sceneGLB,
        currentStore.collisionableItems,
        currentStore.savedViews || [],
        currentStore.lastView,
      )

      if (result.success) {
        const now = new Date()
        setLastSaved(now)

        const currentProjectKey = `${currentClientInfo.nombreProyecto}_${currentClientInfo.idDisenos}`

        lastSavedByProjectRef.current.set(currentProjectKey, now)
      }
    } catch (error: any) {
      console.error('❌ Error en autoguardado:', error)
    } finally {
      isSavingRef.current = false
      setIsSaving(false)
    }
  }, [
    clientInfo,
    items,
    areas,
    totalPrice,
    objectsScene,
    sceneBoundingBoxes,
    savedViews,
    currLayer,
    sceneGLB,
    collisionableItems,
  ])

  useEffect(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current)
      intervalRef.current = null
    }

    if (intervalMinutes <= 0 || !hasProjectName) {
      return
    }

    const intervalMs = intervalMinutes * 60 * 1000

    intervalRef.current = setInterval(() => {
      const currentProject = useAppStore.getState().clientInfo

      if (
        currentProject.nombreProyecto === clientInfo.nombreProyecto &&
        currentProject.idDisenos === clientInfo.idDisenos
      ) {
        autoSave()
      }
    }, intervalMs)

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
        intervalRef.current = null
      }
    }
  }, [intervalMinutes, hasProjectName, clientInfo.nombreProyecto, clientInfo.idDisenos])

  return {
    isSaving,
    lastSaved,
    manualSave: autoSave,
  }
}
